import React, { Fragment, useState } from "react";

import { BrowserNameIcon } from "@common/components/DetailsPane/icons";
import { BaseDialog, useBaseDialog } from "@common/components/Dialog/BaseDialog";
import { ConfirmDialogBody } from "@common/components/Dialog/configurations/ConfirmDialogBody";
import { AssigneeDropdown } from "@common/components/Dropdown/AssigneeDropdown/AssigneeDropdown";
import { PriorityDropdown } from "@common/components/Dropdown/PriorityDropdown/PriorityDropdown";
import { StatusDropdown } from "@common/components/Dropdown/StatusDropdown/StatusDropdown";
import { RowType } from "@common/components/MasterTable/models/MasterTableModel";
import { MergeIntoModalBody } from "@common/components/ShakeTable/ui/MultiactionsPopover/ModalBody/MergeIntoModalBody";
import displayToast from "@common/components/Toast/displayToast";
import { ToggleButton } from "@common/components/ToggleButton/ToggleButton";
import Tooltip from "@common/components/Tooltip/Tooltip";
import environments from "@common/environments";
import { CrashReport } from "@common/models/crashReporting";
import { Issue } from "@common/models/issueTracking";
import { TicketPriority } from "@common/models/TicketPriority.model";
import { TicketStatus } from "@common/models/TicketStatus.model";
import { Flex } from "@common/styles/reusable/Flex/Flex.styles";
import { fireConfetti } from "@common/util/confetti";
import identifiers from "@common/util/identifiers.json";
import { useAppContext } from "@main/context/App/App.context";
import * as Icon from "@main/pages/shared/icons/icons";

import * as Styled from "./DetailsBar.styles";

interface DetailsBarProps {
  details: Issue | CrashReport;
  changeAssignee: (assigneeId: string | null) => void;
  changeStatus: (status: TicketStatus) => void;
  changePriority: (priority: TicketPriority) => void;
  changePublic?: () => Promise<Issue | undefined>;
  deleteTicket: (valueId: string) => void;
  onMergeAction?: (issues: Array<Issue | CrashReport>, issue: Issue | CrashReport) => void;
  clearMerge?: (issues: Array<Issue | CrashReport>, issueId: string) => void;
  showLock?: boolean;
  type: RowType;
}

export const DetailsBar: React.FC<DetailsBarProps> = ({
  details,
  changeAssignee,
  changePriority,
  changeStatus,
  changePublic,
  deleteTicket,
  onMergeAction,
  showLock,
  type,
  clearMerge,
}) => {
  const { wsTeamMembers } = useAppContext();
  const [isStatusDropdownOpened, setIsStatusDropdownOpened] = useState(false);
  const [isPriorityDropdownOpened, setIsPriorityDropdownOpened] = useState(false);

  const mergeModal = useBaseDialog({});
  const deleteTicketModal = useBaseDialog({});

  const handleChangeStatus = (status: TicketStatus) => {
    if (status !== details.status.status) changeStatus(status);
    setIsStatusDropdownOpened(false);
    if (status === TicketStatus.CLOSED) {
      fireConfetti();
    }
  };

  const handleChangePriority = (priority: TicketPriority) => {
    if (priority !== details.priority.priority) changePriority(priority);
    setIsPriorityDropdownOpened(false);
  };

  const handleCopyClick = () => {
    if (!("public_url" in details) || !details.public_url) return;
    navigator.clipboard.writeText(details.public_url);
    displayToast({
      content: "External URL copied to clipboard",
    });
  };

  const handlePublicToggle = async () => {
    if (!changePublic) return;
    changePublic().then((res) => {
      if (res?.public_url) {
        navigator.clipboard.writeText(res.public_url);
        displayToast({ content: "External URL copied to clipboard" });
      }
    });
  };

  if (!(wsTeamMembers || !details || (details.status && details.priority && details.assignee))) {
    return <Fragment />;
  }

  return (
    <>
      <Styled.TicketDetailsBar>
        <Flex
          $gap={1}
          $alignItems="center"
        >
          <AssigneeDropdown
            assigneeId={details.assignee.assignee_id}
            changeAssignee={changeAssignee}
          />
          <StatusDropdown
            status={details.status.status ?? TicketStatus.NEW}
            isOpened={isStatusDropdownOpened}
            setIsOpened={setIsStatusDropdownOpened}
            changeStatus={handleChangeStatus}
          />
          <PriorityDropdown
            priority={details.priority.priority ?? TicketPriority.LOW}
            isOpened={isPriorityDropdownOpened}
            setIsOpened={setIsPriorityDropdownOpened}
            changePriority={handleChangePriority}
          />

          <Styled.DeleteMergeContainer>
            {showLock && (
              <Styled.LockIcon
                data-testid={identifiers["central.column.icon.lock"]}
                $disabled={
                  details.status.status !== TicketStatus.CLOSED && details.status.status !== TicketStatus.LOCKED
                }
              >
                {getCrashLockStatusIcon(changeStatus, details)}
              </Styled.LockIcon>
            )}

            <>
              <Styled.DuplicateContainer
                onClick={() =>
                  details.parent_id ? clearMerge?.([details], details.parent_id) : mergeModal.setOpen(true)
                }
              >
                <Tooltip
                  text={details.parent_id ? "Unmark as duplicate" : "Mark as duplicate of ..."}
                  position="top"
                  style={{ display: "flex", alignItems: "center" }}
                >
                  {details.parent_id ? <Icon.ClearDuplicateIcon /> : <Icon.DuplicateIcon />}
                </Tooltip>
              </Styled.DuplicateContainer>
            </>

            <Styled.DeleteContainer onClick={() => deleteTicketModal.setOpen(true)}>
              <Tooltip
                text="Delete ticket"
                position="top"
                style={{ display: "flex", alignItems: "center", width: "100%", justifyContent: "center" }}
              >
                <Icon.DeleteIcon
                  width={20}
                  height={20}
                  style={{ margin: "0.8rem" }}
                />
              </Tooltip>
            </Styled.DeleteContainer>
          </Styled.DeleteMergeContainer>

          {"public_url" in details && (
            <Tooltip
              text={"Click to make ticket accessible to anyone with the link"}
              position={"top"}
              disabled={!!details.public_url}
            >
              <Styled.ShareTicketContainer>
                <ToggleButton
                  checked={!!details.public_url}
                  onChange={handlePublicToggle}
                  disabled={false}
                />
                <Styled.ShareTicketText style={{ padding: "0 1rem" }}>{"External"}</Styled.ShareTicketText>
                {details.public_url && (
                  <Tooltip
                    text={"Click to copy link"}
                    position={"top"}
                  >
                    <Styled.CopyContainer onClick={handleCopyClick}>
                      <BrowserNameIcon />
                      <Styled.ShareTicketText>{`${environments.shareDomain}/${details.public_key}`}</Styled.ShareTicketText>
                    </Styled.CopyContainer>
                  </Tooltip>
                )}
              </Styled.ShareTicketContainer>
            </Tooltip>
          )}
        </Flex>
      </Styled.TicketDetailsBar>

      {onMergeAction && (
        <BaseDialog
          {...mergeModal}
          noPadding
        >
          <MergeIntoModalBody
            title={`Mark as duplicate of`}
            confirmAction={onMergeAction}
            close={() => mergeModal.setOpen(false)}
            selectedRows={[details]}
            type={type}
          />
        </BaseDialog>
      )}

      <BaseDialog {...deleteTicketModal}>
        <ConfirmDialogBody
          title="Delete ticket?"
          subtitle={`Are you sure you want to delete this ticket? This can't be undone.`}
          confirmAction={() => {
            deleteTicket(details.id);
            deleteTicketModal.setOpen(false);
          }}
          buttonText="Yes, delete"
        />
      </BaseDialog>
    </>
  );
};

const getCrashLockStatusIcon = (changeStatus: (status: TicketStatus) => void, crash?: Issue | CrashReport) => {
  if (!crash || !crash.status) return;
  if (crash?.status?.status === TicketStatus.LOCKED) {
    return (
      <Tooltip
        text="Unlock this crash so it re-opens if it's reported in a newer version."
        position="top"
        style={{ height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <Icon.LockedIcon
          onClick={() => changeStatus(TicketStatus.CLOSED)}
          width={20}
          height={20}
        />
      </Tooltip>
    );
  }

  const isCrashClosed = crash.status.status === TicketStatus.CLOSED;

  const unlockedIconTooltipText = !isCrashClosed
    ? "Close the crash first in order to lock it."
    : "Lock this crash to prevent it from re-opening if it's reported in a newer version.";

  return (
    <Tooltip
      text={unlockedIconTooltipText}
      position="top"
      style={{ height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}
    >
      <Styled.UnlockedIcon
        $disabled={!isCrashClosed}
        onClick={() => {
          if (isCrashClosed) changeStatus(TicketStatus.LOCKED);
        }}
      />
    </Tooltip>
  );
};
